import get from 'lodash/get'
import ResponsiveImage from './ResponsiveImage'
import RichContent from './RichContent'
import Section from './Section'
import { theme } from '../styles/theme'
import { createUseStyles } from '../helpers/createStyles'
import { useCallback, useState, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { getSiteData } from '../store/layoutSlice'
import useSnapshot from '../store/useSnapshot'
import InputContainer from './TradeRegistration/InputContainer'
import Button from './Button'
import { isEmailAddress } from '../helpers/isEmailAddress'
import { useSession, signIn } from 'next-auth/react'
import Link from './Link'
import cn from 'classnames'

export default function TEPAuthPage ({ page, preview }) {
  const styles = useStyles()
  const { data: session, status } = useSession()

  const snap = useSnapshot()
  const locale = getSiteData(snap).locale
  const { klaviyoListId } = page

  const heroTitle = get(page, ['authHeroTitle'])
  const heroImage = get(page, ['authHeroImage'])
  const formTitle = get(page, ['authFormTitle'])

  const tepAuthErrorInvalidEmail = get(page, ['tepAuthErrorInvalidEmail'])
  const tepAuthErrorEmailRequired = get(page, ['tepAuthErrorEmailRequired'])
  const tepAuthErrorUnauthorisedEmail = get(page, ['tepAuthErrorUnauthorisedEmail'])
  const tepAuthErrorSiteNotFound = get(page, ['tepAuthErrorSiteNotFound'])
  const tepAuthErrorApprovedUsersNotFound = get(page, ['tepAuthErrorApprovedUsersNotFound'])

  const errorMessageMap = {
    tepAuthErrorInvalidEmail,
    tepAuthErrorEmailRequired,
    tepAuthErrorUnauthorisedEmail,
    tepAuthErrorSiteNotFound,
    tepAuthErrorApprovedUsersNotFound
  }

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const formRef = useRef()

  if (status === 'authenticated') {
    if (!success) {
      console.log('TEP user logged in, session:', session)
      setSuccess(true)
      setLoading(true)
    }
    window.location.href = '/trade-exclusive-program'// Explicitly not using router.push to avoid an infinite loop bug
  }

  const methods = useForm({
    mode: 'onTouched',
    defaultValues: {
      email_address: ''
    }
  })

  const onSubmit = useCallback(async (data) => {
    if (!isEmailAddress(data.email_address)) {
      const errorMessage = errorMessageMap.tepAuthErrorInvalidEmail
      methods.setError('email_address', { type: 'invalid_email', message: errorMessage }, { shouldFocus: true })
      setError({ invalid_email: errorMessage })
      return
    }
    setLoading(true)

    const email = data.email_address
    const response = await signIn('credentials',
      {
        email,
        password: email,
        // The page where you want to redirect to after a
        // successful login
        redirect: false
      }
    )
    if (response.ok) {
      setSuccess(true)
      window.location.href = '/trade-exclusive-program'// Explicitly not using router.push to avoid an infinite loop bug
    } else {
      const message = response.error
      const errorMessage = errorMessageMap[message] || message
      methods.setError('email_address', { type: 'server_error', errorMessage }, { shouldFocus: true })
      setError({ server_error: errorMessage })
    }
    setLoading(false)
  }, [locale, klaviyoListId])

  return (
    <Section className={styles.section} noBottomMargin fullWidth>
      <div className={styles.tep_auth_hero}>
        <ResponsiveImage image={heroImage} className={styles.image} />
        <div className={styles.heroContainer}>
          <h1 className={styles.heroTitle}>{heroTitle}</h1>
          <FormProvider {...methods}>
            <div className={styles.tep_auth_form}>

              {success && (
                <div className={styles.root}>
                  <h2>Login Successful</h2>
                </div>
              )}
              {!success && (
                <form onSubmit={methods.handleSubmit(onSubmit)} ref={formRef}>
                  <h1 className={styles.formTitle}>
                    <RichContent className={styles.formTitle} content={formTitle} />
                  </h1>

                  <div className={styles.loginContainer}>
                    <label htmlFor='email_address' className={styles.height36}>Username</label>
                    <InputContainer containerClassName={styles.roundedInput} lightTheme name='email_address' required placeholder='Email Address' validationMessages={error} />
                    <Button disabled={loading || status === 'authenticated'} type='submit' className={cn(styles.roundedButton, styles.whiteBG, 'desktopOnly')}>{loading ? 'Logging In...' : 'Enter'}</Button>
                  </div>

                </form>
              )}
            </div>
            <div className={styles.buttonContainer}>
              <Button disabled={loading || status === 'authenticated'} type='submit' className={cn(styles.roundedButton, styles.whiteBG, 'mobileOnly')} onClick={methods.handleSubmit(onSubmit)}>{loading ? 'Logging In...' : 'Enter'}</Button>
              <Button className={styles.roundedButton} to='trade-registration-form'>Register for a Trade Account</Button>
              <Link className={styles.whiteText} to='/' aria-label='Armadillo'>
                Back to Armadillo Shop
              </Link>
            </div>
          </FormProvider>
        </div>

      </div>

    </Section>

  )
}

const useStyles = createUseStyles({
  section: {
    height: '100lvh',
    maxHeight: '100lvh',
    overflow: 'hidden',
    width: '100%',
    position: 'absolute',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: '1 1 0',
    marginLeft: 0,
    marginBottom: 0,
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row'
    },
    '& > *': {
      flex: '1 1 0'
    }
  },
  image: {
    filter: 'brightness(50%)',
    height: '100%'
  },
  heroContainer: {
    color: theme.colors.white,
    zIndex: theme.zIndex.imageContent,
    width: '100%',
    height: '100%',

    position: 'absolute',
    top: 0,
    right: 0,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(8),
    paddingTop: theme.spacing(16),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',

    '& h1': {
      color: theme.colors.white,
      fontFamily: 'Sohne-Breit-Leicht',
      fontSize: '12px !important',
      fontStyle: 'normal',
      fontWeight: 400,
      letterSpacing: '4px',
      textTransform: 'uppercase'
    },

    '& p': {
      color: theme.colors.white,
      fontFamily: 'Söhne Leicht',
      fontSize: 16,
      fontStyle: 'normal',
      fontWeight: 300
    },

    [theme.breakpoints.up('md')]: {

      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(16),

      '& h1': {
        fontSize: 40,
        fontWeight: 400
      },

      '& p': {
        fontSize: '24px',
        fontWeight: 300
      }

    }

  },

  tep_auth_hero: {
    width: '100%',
    flex: '2 1 0',
    position: 'relative',
    maxHeight: '100vh',
    overflow: 'hidden'
  },

  tep_auth_form: {
    maxHeight: '100vh',
    flex: '1 1 0',

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',

    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(4),

    '& h1': {
      '& p': {
        color: theme.colors.white,
        fontFamily: 'Iskry',
        fontSize: 20,
        fontStyle: 'normal',
        fontWeight: 700,
        textAlign: 'center',
        [theme.breakpoints.up('md')]: {
          fontSize: 30
        },
        textTransform: 'none'
      }
    },

    '& p': {
      color: theme.colors.black,
      fontFamily: 'Söhne Leicht',
      fontSize: 14,
      fontStyle: 'normal',
      fontWeight: 300
    },

    '& form': {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: theme.spacing(4)
      // alignItems: 'center',
    },

    [theme.breakpoints.up('md')]: {

      paddingLeft: theme.spacing(8),
      paddingRight: theme.spacing(8),
      paddingTop: theme.spacing(8),

      '& h1': {
        fontSize: 32,
        fontWeight: 400
      },

      '& p': {
        fontSize: 14,
        fontWeight: 300
      }

    }

  },

  loginContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'top',
    '& > *:nth-child(2)': {
      flex: '2 1 0'
    },
    '& > *:first-child': {
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'block'
      }
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row'
    },
    gap: theme.spacing(3)
  },

  buttonContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    '& button': {
      width: '100%'
    }
  },

  formAddendum: {
    opacity: 0.5
  },

  roundedInput: {
    '& input': {
      border: `1px solid ${theme.colors.white}`,
      borderRadius: '90px',
      paddingLeft: theme.spacing(2),
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  },

  roundedButton: {
    border: `1px solid ${theme.colors.white}`,
    color: 'white !important',
    borderRadius: '90px',
    minHeight: '36px !important',
    height: 36,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'fit-content'
    }
  },

  whiteBG: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)'
  },

  whiteText: {
    color: theme.colors.white
  },

  height36: {
    height: 36,
    alignContent: 'center'
  }

  // emailInput
  // ::placeholder {
  //   color: red;
  //   opacity: 1; /* Firefox */
  // }

  // ::-ms-input-placeholder { /* Edge 12 -18 */
  //   color: red;
  // }
})
